import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

import { Badge, Button, ButtonGroup, Tooltip } from '@chakra-ui/react'

import { SectionHeader } from '@/components/Elements'

//Constant

//Helper
import {
  CostBudgetWorkflowInitiatorType,
  CostBudgetWorkflowStatus,
  getInitialOfferCostBudgetDetail,
  SourcingDecisionWorkflowStatus,
} from '@/features/offerCostBudgets/constants/offerCostBudget.constant'
import { offerSourcingGroupPolicyType } from '@/features/offers/constants/offerEditRights/offerSourcingEditRight'
import { selectOfferDetail, selectOfferPermissions } from '@/features/offers/slices/offerDetail.slice'
import usePrintCaptureMode from '@/features/offers/hooks/usePrintCaptureMode'
import CostBudgetTabs from './CostBudget/CostBudgetTabs'
import SourcingGeneralInfo from './SourcingGeneralInfo'

const renderCostBudgetStatusBadge = (costBudget, isProcurementSide = false) => {
  if (!costBudget?.detail?.isActive) {
    const inactiveReason = costBudget?.detail?.inactiveReason ?? '(-)'
    return (
      <Tooltip label={`Reason: ${inactiveReason}`}>
        <Badge>Inactive</Badge>
      </Tooltip>
    )
  }

  if (costBudget?.detail?.workflow?.status === CostBudgetWorkflowStatus.OPEN) {
    const issues = costBudget?.detail?.workflow?.issues
    if (!Array.isArray(issues) || issues.length === 0) {
      return <></>
    }

    const lastIssue = issues[issues.length - 1]
    const pendingReplyUserType = lastIssue.pendingReplyUserType

    if (
      (pendingReplyUserType === CostBudgetWorkflowInitiatorType.PROCUREMENT && isProcurementSide) ||
      (pendingReplyUserType === CostBudgetWorkflowInitiatorType.TECHNICAL && !isProcurementSide)
    ) {
      return <Badge colorScheme="red">{SourcingDecisionWorkflowStatus.ACTION_REQUIRED}</Badge>
    }
    return <Badge colorScheme="green">{SourcingDecisionWorkflowStatus.UPCOMING}</Badge>
  }

  return <></>
}

const OfferSourcingTab = () => {
  const { role } = useSelector(selectOfferDetail)

  const offerPermissions = useSelector(selectOfferPermissions)

  // Local State

  const costBudgetRef = useRef([])

  // Hooks

  const { getValues, setValue } = useFormContext()
  const dispatch = useDispatch()

  const offerSubmissionDate = useWatch({ name: `offerSubmissionDate` })
  const baseQuotationNumber = useWatch({ name: `baseQuotationNumber` })
  const bookingCreateDate = useWatch({ name: `booking.bookingCreateDate` })
  const firstSubSystemName = useWatch({ name: `subSystems.0.projectName` })
  const watchedSubSystems = useWatch({ name: 'subSystems' })
  const subSystems = useMemo(() => watchedSubSystems ?? [], [watchedSubSystems])

  const [selectedSubSystemIndex, setSelectedSubSystemIndex] = useState(null)
  const isPrintCaptureMode = usePrintCaptureMode()
  // Print capture should always go through every cost budget, regardless of whatever subsystem
  // filter the user had active when they opened the print modal.
  const effectiveSelectedSubSystemIndex = isPrintCaptureMode ? null : selectedSubSystemIndex

  const costBudgetWatchFields = useWatch({ name: 'sourcing.costBudgets' })
  const {
    fields: costBudgetFields,
    append: appendCostBudget,
    remove: removeCostBudget,
    insert: insertCostBudget,
  } = useFieldArray({ name: 'sourcing.costBudgets' })

  const controlledCostBudgetFields = costBudgetFields.map((field, index) => {
    return {
      ...field,
      ...costBudgetWatchFields[index],
    }
  })

  const filteredCostBudgetItems = useMemo(() => {
    if (effectiveSelectedSubSystemIndex === null) {
      return controlledCostBudgetFields.map((costBudget, originalIndex) => ({
        costBudget,
        originalIndex,
      }))
    }

    const selectedSubSystem = subSystems[effectiveSelectedSubSystemIndex]
    const selectedSubSystemName = selectedSubSystem?.projectName ?? ''

    return controlledCostBudgetFields
      .map((costBudget, originalIndex) => ({
        costBudget,
        originalIndex,
      }))
      .filter((item) => item.costBudget?.detail?.subSystem === selectedSubSystemName)
  }, [controlledCostBudgetFields, effectiveSelectedSubSystemIndex, subSystems])

  //Translated Variable
  const canAddCostBudget = offerPermissions[offerSourcingGroupPolicyType.SOURCING_ADD_REMOVE_COST_BUDGET]?.edit ?? false

  const canRemoveCostBudget =
    offerPermissions[offerSourcingGroupPolicyType.SOURCING_ADD_REMOVE_COST_BUDGET]?.edit ?? false
  const canEditCostBudget = offerPermissions[offerSourcingGroupPolicyType.SOURCING_COST_BUDGET]?.edit ?? false
  const hasSelectedSubSystem = selectedSubSystemIndex !== null

  useEffect(() => {
    if (offerSubmissionDate) {
      const sourcingDueDate = getValues('sourcing.sourcingDueDate')

      if (!sourcingDueDate) {
        //Only set value when due date is empty
        let newDueDate = moment(offerSubmissionDate).subtract(2, 'day')
        if (newDueDate.day() === 6) {
          //Sat
          newDueDate = newDueDate.subtract(1, 'day')
        } else if (newDueDate.day() === 0) {
          newDueDate = newDueDate.subtract(2, 'day')
        }

        setValue('sourcing.sourcingDueDate', newDueDate.toDate(), { shouldDirty: true })
      }
    }
  }, [getValues, offerSubmissionDate, setValue])

  const handleAddCostBudget = useCallback(async () => {
    const allRFQCodes = controlledCostBudgetFields
      .map((costBudget) => costBudget.detail.RFQCode ?? '')
      .filter((code) => code !== '')
    const lastRFQNumber = allRFQCodes.reduce((max, code) => {
      const match = code.match(/-(\d+)$/)
      const number = match ? parseInt(match[1], 10) : 0
      return Math.max(max, number)
    }, 0)

    console.log('allRFQCodes', allRFQCodes)
    console.log('lastRFQNumber', lastRFQNumber)

    const newRFQNumber = lastRFQNumber + 1

    const newRFQCode = `${baseQuotationNumber}-RFQ-${newRFQNumber}`
    const selectedSubSystemName =
      selectedSubSystemIndex !== null ? subSystems[selectedSubSystemIndex]?.projectName : firstSubSystemName

    appendCostBudget({
      detail: getInitialOfferCostBudgetDetail(selectedSubSystemName, newRFQCode),

      firstProcurementOfficerSelection: {
        vendor: null,
        isReference: false,
        uploadHistoryIndex: -1,
        remark: '',
      },
      firstTechnicalSelection: {
        vendor: null,
        isReference: false,
        uploadHistoryIndex: -1,
        remark: '',
      },
      secondTechnicalSelection: {
        vendor: null,
        isReference: false,
        uploadHistoryIndex: -1,
        remark: '',
      },
      PR: {
        vendorSelections: [
          {
            vendor: null,
            isReference: false,
            uploadHistoryIndex: -1,
            remark: '',
            quotationDetails: [],
          },
        ],
        submissions: [],
      },
    })
  }, [
    appendCostBudget,
    baseQuotationNumber,
    controlledCostBudgetFields,
    firstSubSystemName,
    selectedSubSystemIndex,
    subSystems,
  ])

  return (
    <>
      <SourcingGeneralInfo />

      <SectionHeader
        title={'Cost Budgets'}
        hasDivider={false}
        mt={0}
        rightElement={
          !isPrintCaptureMode &&
          canAddCostBudget &&
          selectedSubSystemIndex !== null && (
            <Button colorScheme="green" onClick={handleAddCostBudget}>
              Add
            </Button>
          )
        }
      />

      {!isPrintCaptureMode && Array.isArray(subSystems) && subSystems.length > 0 && (
        <ButtonGroup size="sm" flexWrap="wrap" gap="2" mb="3">
          <Button
            colorScheme={selectedSubSystemIndex === null ? 'blue' : 'gray'}
            variant={selectedSubSystemIndex === null ? 'solid' : 'outline'}
            onClick={() => setSelectedSubSystemIndex(null)}
          >
            All
          </Button>
          {subSystems.map((subSystem, index) => (
            <Button
              key={index}
              colorScheme={selectedSubSystemIndex === index ? 'blue' : 'gray'}
              variant={selectedSubSystemIndex === index ? 'solid' : 'outline'}
              onClick={() => setSelectedSubSystemIndex(selectedSubSystemIndex === index ? null : index)}
            >
              {`1.${index + 1}`} {subSystem.projectName}
            </Button>
          ))}
        </ButtonGroup>
      )}

      <CostBudgetTabs
        costBudgets={filteredCostBudgetItems.map((item) => item.costBudget)}
        costBudgetIndices={filteredCostBudgetItems.map((item) => item.originalIndex)}
        removeCostBudget={removeCostBudget}
        canEditCostBudget={canEditCostBudget && hasSelectedSubSystem}
        canRemoveCostBudget={canRemoveCostBudget && hasSelectedSubSystem}
        hasSelectedSubSystem={hasSelectedSubSystem}
      />
    </>
  )
}

export default OfferSourcingTab
